import { Button, STYLE_NONE, TYPE_LINK } from '@eventbrite/eds-button';
import { DataTable } from '@eventbrite/eds-data-table';
import { Icon } from '@eventbrite/eds-icon';
import {
    CheckChunky,
    CrossChunky,
    InfoChunky,
} from '@eventbrite/eds-iconography';
import { Modal } from '@eventbrite/eds-modal';
import { gettext } from '@eventbrite/i18n';
import { css, cx } from 'emotion';
import isEqual from 'lodash/isEqual';
import React, { useState } from 'react';
import { BrowserTypes, COLORS } from '../../../constants';
import { getUserBrowser } from '../../../utils';

interface ZoomLimitationsProps {
    isVisible?: boolean;
}

export const ZoomLimitations: React.FC<ZoomLimitationsProps> = ({
    isVisible,
}) => {
    const [isShown, setIsShown] = useState<boolean>(false);
    const iconSize = 'xsmall';

    const openModal = () => setIsShown(true);
    const closeModal = () => setIsShown(false);

    const check = (
        <Icon type={<CheckChunky />} color="grey-700" size={iconSize} />
    );
    const cross = (
        <Icon type={<CrossChunky />} color="ui-red" size={iconSize} />
    );

    const infoMessage = () => {
        const text = gettext(
            'Only available for webinar attendees if using Firefox or Safari. Anyone can join computer audio on Firefox if they are using Linux.',
        );

        return (
            <div className={cx(styles.infoMessage)}>
                <Icon
                    type={<InfoChunky />}
                    color="ui-blue--hover"
                    size={iconSize}
                />
                <span className="eds-l-pad-all-4 eds-text-color--u-800 eds-bg-color--background eds-fx--fade-in-up">
                    {text}
                </span>
            </div>
        );
    };

    const COLUMNS = [
        {
            fieldName: 'feature',
            fieldLabel: '',
            isEditable: false,
            isSortable: false,
        },
        {
            fieldName: 'chrome',
            fieldLabel: 'Chrome, Firefox, Edge',
            isEditable: false,
            isSortable: false,
        },
        {
            fieldName: 'safari',
            fieldLabel: 'Safari',
            isEditable: false,
            isSortable: false,
        },
    ];

    const ITEMS = [
        {
            id: 1,
            feature: gettext('Share video'),
            chromeDisplay: check,
            safariDisplay: check,
        },
        {
            id: 2,
            feature: gettext('Share screen'),
            chromeDisplay: check,
            safariDisplay: cross,
        },
        {
            id: 3,
            feature: gettext('Join computer audio'),
            chromeDisplay: check,
            safariDisplay: infoMessage(),
        },
        {
            id: 4,
            feature: gettext('Join audio by phone'),
            chromeDisplay: check,
            safariDisplay: check,
        },
        {
            id: 5,
            feature: gettext('Meeting host controls'),
            chromeDisplay: check,
            safariDisplay: check,
        },
        {
            id: 6,
            feature: gettext('View shared video'),
            chromeDisplay: check,
            safariDisplay: check,
        },
        {
            id: 7,
            feature: gettext('View shared screen'),
            chromeDisplay: check,
            safariDisplay: check,
        },
        {
            id: 8,
            feature: gettext('Request remote control'),
            chromeDisplay: check,
            safariDisplay: check,
        },
        {
            id: 9,
            feature: gettext('In-meeting chat'),
            chromeDisplay: check,
            safariDisplay: check,
        },
        {
            id: 10,
            feature: gettext('Closed captioning'),
            chromeDisplay: check,
            safariDisplay: check,
        },
        {
            id: 11,
            feature: gettext('Webinar Q&A'),
            chromeDisplay: check,
            safariDisplay: check,
        },
    ];

    const renderModalContent = () => {
        const description = gettext(
            'You can join this event in your Eventbrite window, but certain browsers have limitations:',
        );
        return (
            <div>
                <p className="eds-text-bm eds-text-color--ui-800">
                    {description}
                </p>
                <div className={cx(styles.table, 'eds-l-mar-top-6')}>
                    <DataTable items={ITEMS} columns={COLUMNS} />
                </div>
            </div>
        );
    };

    const renderModal = () => {
        const title = gettext('Embedded Zoom experience');
        const primaryText = gettext('Done');
        return (
            <Modal
                isShown={isShown}
                title={title}
                onClose={closeModal}
                onPrimaryAction={closeModal}
                primaryText={primaryText}
                primaryType={TYPE_LINK}
            >
                {renderModalContent()}
            </Modal>
        );
    };

    const renderContent = () => {
        const browser = getUserBrowser();
        const hasLimitations = isEqual(browser, BrowserTypes.safari);

        return isVisible && hasLimitations ? (
            <>
                <Button
                    __containerClassName={cx(
                        styles.button,
                        'eds-l-mar-top-3 eds-text-bs eds-text-color--control--active',
                    )}
                    style={STYLE_NONE}
                    onClick={openModal}
                >
                    <Icon
                        type={<InfoChunky />}
                        color="ui-blue--active"
                        size={iconSize}
                    />
                    <span className="eds-l-mar-left-2">
                        {gettext('Which should I choose?')}
                    </span>
                </Button>
                {renderModal()}
            </>
        ) : null;
    };

    return renderContent();
};

const styles = {
    button: css`
        display: flex;
        align-items: center;

        :hover {
            color: ${COLORS.uiBlueHover};

            .eds-vector-image svg path {
                fill: ${COLORS.uiBlueHover} !important;
            }
        }
    `,
    infoMessage: css`
        position: relative;
        cursor: pointer;

        span {
            display: none;
            text-align: left;
            min-width: 200px;
            position: absolute;
            top: 24px;
            right: 0;
            box-shadow: ${COLORS.uiElevated};
            cursor: default;
        }

        :hover span {
            display: block;
        }
    `,
    table: css`
        .eds-data-table-header__column:not(:first-child),
        .eds-data-table-list-item__column:not(:first-child) {
            text-align: center;
            width: 88px;
            white-space: normal;
        }

        .eds-data-table-header {
            height: 46px;
        }

        .eds-data-table-header__column:not(:first-child) span {
            display: block;
            text-align: center;
            font-size: 12px;
            line-height: 16px;
            font-weight: 600;
            color: ${COLORS.ui800};
        }

        .eds-data-table-header__column-button {
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
        }

        .eds-data-table-list-item__column:first-child {
            font-size: 12px;
            line-height: 16px;
            color: ${COLORS.ui600};
        }

        .eds-data-table-list-item__column {
            border-bottom: 1px solid ${COLORS.ui200};
        }

        .eds-data-table-list-item__column:not(:last-child) {
            border-right: 1px solid ${COLORS.ui100};
        }

        .eds-data-table-list-item__column:not(:first-child, :nth-child(3)) {
            background: ${COLORS.uiCellBg};
        }
    `,
};
